/* Approved holo-card layers. Existing Shopify links and hold/drag handlers remain authoritative. */
import { getPhoneCardProfile, phoneMotionMedia } from './loc-motion-profiles.js?v=approved-20260911';
const instances = new Map();
// User-approved preview: inward character parallax with restrained contour light.
const DEPTH = -.65, CONTOUR_GLOW = .15, SUBJECT_GAIN = 2.4;
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
let rendererModule;
function setup(card) {
  if (instances.has(card) || reduced.matches) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'loc-layered-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  card.append(canvas);
  let renderer, frame = 0, disposed = false, visible = false;
  let x = 0, y = 0, tx = 0, ty = 0;
  let motionActive = false, drawDepth = DEPTH, drawGlow = CONTOUR_GLOW, drawPower = 1;
  const abort = new AbortController();
  const observer = new MutationObserver(refresh);
  function cleanup() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(frame);
    observer.disconnect(); abort.abort(); visibility.unobserve(card);
    renderer?.dispose();
    canvas.getContext('webgl')?.getExtension('WEBGL_lose_context')?.loseContext();
    canvas.remove(); delete card.dataset.layeredReady; instances.delete(card);
  }
  function refresh() {
    motionActive = card.classList.contains('is-motion-active') && !card.classList.contains('is-pointer-active');
    const active = card.classList.contains('is-pointer-active') || motionActive;
    const profile = getPhoneCardProfile(card);
    drawDepth = profile?.depth ?? DEPTH; drawGlow = profile?.glow ?? CONTOUR_GLOW; drawPower = profile?.foil ?? 1;
    tx = active ? parseFloat(card.style.getPropertyValue('--ip-tilt-x')) || 0 : 0;
    ty = active ? parseFloat(card.style.getPropertyValue('--ip-tilt-y')) || 0 : 0;
    if (visible && renderer && !document.hidden) {
      // Motion input is already smoothed once. Draw in the same frame as its CSS
      // pose, without the extra adapter easing used by desktop hover/hand dragging.
      if (motionActive) { cancelAnimationFrame(frame); paint(); }
      else if (!frame) frame = requestAnimationFrame(paint);
    }
  }
  function paint() {
    frame = 0;
    if (disposed || !visible || !renderer || document.hidden) return;
    if (motionActive) { x = tx; y = ty; }
    else { x += (tx - x) * .19; y += (ty - y) * .19; }
    renderer.draw(x, y, drawPower, drawDepth, drawGlow);
    if (Math.abs(tx - x) + Math.abs(ty - y) > .003) frame = requestAnimationFrame(paint);
  }
  instances.set(card, {cleanup, setVisible(value) { visible = value; if (value) refresh(); else {cancelAnimationFrame(frame); frame = 0;} }});
  visibility.observe(card);
  // Preserve normal scrolling/taps; only a deliberate existing 280ms hold rotates.
  card.addEventListener('touchmove', event => {
    if (event.touches.length === 1 && card.classList.contains('is-pointer-active')) event.preventDefault();
  }, {passive:false, signal:abort.signal});
  card.addEventListener('dragstart', event => event.preventDefault(), {signal:abort.signal});
  canvas.addEventListener('webglcontextlost', cleanup, {once:true, signal:abort.signal});
  document.addEventListener('visibilitychange', refresh, {signal:abort.signal});
  phoneMotionMedia.addEventListener('change', refresh, {signal:abort.signal});
  rendererModule ||= import(card.dataset.layerRenderer);
  rendererModule.then(({createCardRenderer}) => createCardRenderer(canvas, {
    character: card.dataset.layerCharacter, background: card.dataset.layerBackground,
    ui: card.dataset.layerUi, structure: card.dataset.layerStructure
  }, {subjectGain: SUBJECT_GAIN, ...(card.dataset.locLayered === 'one_piece' ? {backgroundScale:.9, backgroundTravel:.06} : {})}))
    .then(value => {
      if (disposed) {value.dispose(); return;}
      renderer = value;
      const profile = getPhoneCardProfile(card);
      renderer.draw(0, 0, profile?.foil ?? 1, profile?.depth ?? DEPTH, profile?.glow ?? CONTOUR_GLOW);
      card.dataset.layeredReady = 'true';
      observer.observe(card, {attributes:true, attributeFilter:['style','class']});
      refresh();
    }).catch(() => {cleanup();}); // Original image/link stays usable if assets or WebGL fail.
}
const visibility = new IntersectionObserver(entries => entries.forEach(e => instances.get(e.target)?.setVisible(e.isIntersecting)), {rootMargin:'100px'});
const lazy = new IntersectionObserver(entries => entries.forEach(e => {if(e.isIntersecting){lazy.unobserve(e.target);setup(e.target);}}), {rootMargin:'200px'});
function scan(root=document) { root.querySelectorAll('[data-loc-layered]').forEach(card => lazy.observe(card)); }
scan();
document.addEventListener('shopify:section:load', e => scan(e.target));
document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-loc-layered]').forEach(card => {lazy.unobserve(card);instances.get(card)?.cleanup();}));
reduced.addEventListener('change', () => {if(reduced.matches) [...instances.values()].forEach(i=>i.cleanup()); else scan();});
